import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Menu, Accessibility, LogOut, User, LayoutDashboard, Plus, Heart, MapPinned } from "lucide-react";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/categories", label: "Categories" },
  { to: "/search", label: "Search" },
  { to: "/coverage", label: "Coverage" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const authed = user && user !== false;

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA] text-gray-900">
      <a href="#main" className="skip-link" data-testid="skip-link">Skip to main content</a>

      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-xl" data-testid="brand-link">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-blue-600 text-white">
              <Accessibility className="w-5 h-5" aria-hidden="true" />
            </span>
            <span>Access Local</span>
          </Link>

          <nav aria-label="Primary" className="hidden md:flex items-center gap-1">
            {navItems.map(n => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
                className={({isActive}) => `px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${isActive ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-100"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {authed ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="user-menu-trigger" className="rounded-full border-2 border-gray-900">
                    <User className="w-4 h-4 mr-2" /> {user.name?.split(" ")[0] || "Account"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Signed in as<br/><span className="font-semibold">{user.email}</span></DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild data-testid="menu-dashboard"><Link to="/dashboard"><LayoutDashboard className="w-4 h-4 mr-2"/>Dashboard</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="menu-saved"><Link to="/saved"><Heart className="w-4 h-4 mr-2"/>Saved places</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="menu-trips"><Link to="/trips"><MapPinned className="w-4 h-4 mr-2"/>Trip lists</Link></DropdownMenuItem>
                  {(user.role === "member" || user.role === "admin") && (
                    <DropdownMenuItem asChild data-testid="menu-nominate"><Link to="/nominate"><Plus className="w-4 h-4 mr-2"/>Nominate a place</Link></DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={doLogout} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2"/>Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" asChild data-testid="header-login-btn"><Link to="/login">Log in</Link></Button>
                <Button asChild className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="header-register-btn"><Link to="/register">Join us</Link></Button>
              </>
            )}
          </div>

          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="mobile-menu-trigger">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <nav className="mt-8 flex flex-col gap-1">
                  {navItems.map(n => (
                    <NavLink key={n.to} to={n.to} end={n.to === "/"}
                      className={({isActive}) => `px-3 py-3 rounded-lg text-base font-medium ${isActive ? "bg-gray-900 text-white" : "text-gray-800 hover:bg-gray-100"}`}>
                      {n.label}
                    </NavLink>
                  ))}
                  <div className="border-t border-gray-200 my-2" />
                  {authed ? (
                    <>
                      <Link to="/dashboard" className="px-3 py-3 rounded-lg text-base font-medium hover:bg-gray-100">Dashboard</Link>
                      <Link to="/saved" className="px-3 py-3 rounded-lg text-base font-medium hover:bg-gray-100">Saved places</Link>
                      <Link to="/trips" className="px-3 py-3 rounded-lg text-base font-medium hover:bg-gray-100">Trip lists</Link>
                      {(user.role === "member" || user.role === "admin") && (
                        <Link to="/nominate" className="px-3 py-3 rounded-lg text-base font-medium hover:bg-gray-100">Nominate</Link>
                      )}
                      <button onClick={doLogout} className="text-left px-3 py-3 rounded-lg text-base font-medium hover:bg-gray-100">Log out</button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className="px-3 py-3 rounded-lg text-base font-medium hover:bg-gray-100">Log in</Link>
                      <Link to="/register" className="px-3 py-3 rounded-lg text-base font-medium bg-blue-600 text-white">Join us</Link>
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main id="main" className="flex-1">{children}</main>

      <footer className="bg-gray-900 text-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 font-heading font-bold text-lg text-white">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600"><Accessibility className="w-4 h-4"/></span>
              Access Local
            </div>
            <p className="mt-3 text-sm text-gray-400 leading-relaxed">
              Community-powered accessibility ratings for Swindon, Wiltshire and beyond.
            </p>
          </div>
          <div>
            <h4 className="text-white font-heading font-semibold mb-3">Explore</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/categories" className="hover:text-white">Categories</Link></li>
              <li><Link to="/search" className="hover:text-white">Search venues</Link></li>
              <li><Link to="/how-it-works" className="hover:text-white">How it works</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-heading font-semibold mb-3">Community</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/register" className="hover:text-white">Join as a member</Link></li>
              <li><Link to="/nominate" className="hover:text-white">Nominate a place</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact us</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-heading font-semibold mb-3">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
              <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              <li><Link to="/about" className="hover:text-white">About</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-xs text-gray-500 flex flex-col sm:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} Access Local. All rights reserved.</span>
            <span>Made with care for accessible communities.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
